import React, { useState, useEffect } from "react";
import styles from "./Avatar.module.css";
import { sanitizeAvatarSrc } from "../../utils/brandAvatar";

const sizeMap = {
  sm: "sm",
  md: "md",
  lg: "lg",
};

/**
 * Derive display initials from a person's details.
 *
 * Mirrors the convention used elsewhere in the app (ClanWorkspaceShell /
 * get-started): first letter of first name + first letter of last name,
 * falling back to the first two letters of a username or email, then "?".
 *
 * @param {object} [opts]
 * @param {string} [opts.firstName]
 * @param {string} [opts.lastName]
 * @param {string} [opts.name]     - Full / display name (used when first/last absent)
 * @param {string} [opts.username]
 * @param {string} [opts.email]
 * @returns {string} 1–2 uppercase characters
 */
export function deriveInitials({
  firstName = "",
  lastName = "",
  name = "",
  username = "",
  email = "",
} = {}) {
  const first = `${firstName || ""}`.trim();
  const last = `${lastName || ""}`.trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";
  }

  const full = `${name || ""}`.trim();
  if (full) {
    return full
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase();
  }

  const handle = `${username || ""}`.trim();
  if (handle) return handle.slice(0, 2).toUpperCase();

  const mail = `${email || ""}`.trim();
  if (mail) return mail.slice(0, 2).toUpperCase();

  return "?";
}

/**
 * Circular avatar image.
 *
 * When there is no usable `src` (or the image fails to load) the avatar
 * falls back to the user's initials inside a brand-tinted circle — never a
 * generic stock photo or the Uniflo brand mark.
 *
 * @param {object}  props
 * @param {string}  [props.src]       - Image URL
 * @param {string}  [props.alt]       - Alt text
 * @param {string}  [props.initials]  - Precomputed initials (overrides derivation)
 * @param {string}  [props.name]      - Full name used to derive initials
 * @param {string}  [props.firstName] - First name used to derive initials
 * @param {string}  [props.lastName]  - Last name used to derive initials
 * @param {string}  [props.username]  - Username fallback for initials
 * @param {string}  [props.email]     - Email fallback for initials
 * @param {"sm"|"md"|"lg"} [props.size] - sm (40px) | md (60px) | lg (130px)
 * @param {boolean} [props.online]    - Show green online dot
 * @param {string}  [props.className] - Extra class names
 * @param {function} [props.onClick]  - Click handler
 */
export default function Avatar({
  src,
  alt = "",
  initials,
  name,
  firstName,
  lastName,
  username,
  email,
  size = "sm",
  online = false,
  className = "",
  onClick,
}) {
  const sizeClass = styles[sizeMap[size]] || styles.sm;
  const [imgFailed, setImgFailed] = useState(false);

  // Strip Uniflo brand placeholders (mark/logo/touch defaults): they are not a
  // person's photo, so they must fall through to the initials avatar.
  const cleanSrc = sanitizeAvatarSrc(src);

  // Reset the error state whenever the source changes so a new (valid) photo
  // can render after a previous one failed.
  useEffect(() => {
    setImgFailed(false);
  }, [cleanSrc]);

  const resolvedInitials =
    (typeof initials === "string" && initials.trim()) ||
    deriveInitials({ firstName, lastName, name: name || alt, username, email });

  const showImage = Boolean(cleanSrc) && !imgFailed;

  return (
    <span
      className={`${styles.wrapper} ${sizeClass} ${className}`.trim()}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter") onClick(e);
            }
          : undefined
      }
    >
      {showImage ? (
        <img
          className={styles.img}
          src={cleanSrc}
          alt={alt}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span
          className={styles.fallback}
          role="img"
          aria-label={alt || resolvedInitials}
        >
          {resolvedInitials}
        </span>
      )}
      {online && <span className={styles.onlineDot} aria-label="Online" />}
    </span>
  );
}
